import {
  Box,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  HStack,
  Icon,
  Image,
  Input,
  SlideFade,
  Spacer,
} from '@chakra-ui/react';
import { httpClient } from 'apis';
import { PATH_NAME } from 'configs';
import { FC, useLayoutEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { isEmail, secondToHours } from 'utils';

import { authActions, isTwoFaVerifiedSelector } from 'store/modules/auth';

import { authApi } from 'apis/auth';

import { Card, PasswordInput, PrimaryButton, Typography } from 'components/atoms';

import { useDeviceMode, useRouter, useToasts } from 'hooks/common';

import { CaretRightIcon, JodTextWhite, LogoIcon } from 'assets/icons';

import { authStorage, hqStorage, userStorage } from 'configs/browser-storage';
import { rememberMeStorage } from 'configs/browser-storage';
import i18n from 'configs/i18n';

import { Language } from 'constant/language';
import { setHq } from 'store/modules/superHq-user';
import { colors } from 'themes/foundations/colors';
import { addHexOpacity } from 'utils/opacity';

import PuzzleBackground from '../assets/icons/PuzzleBackground.svg';
import BottomSpiral from '../assets/icons/BottomSpiral.svg';
import WelcomeToJodTitle from '../assets/icons/WelcomeToJodTitle.svg';
import { DeviceMode } from 'types';
import { Role } from 'models/user';

type FormData = {
  email: string;
  password: string;
  remember: boolean;
  tfa_code: string;
};

const Login: FC = (props) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { notiFailed } = useToasts();
  const isTwoFaVerified = useSelector(isTwoFaVerifiedSelector);

  const react2fa = process.env.REACT_APP_TFA_AUTHENTICATION;
  const device = useDeviceMode();

  const partnerRoles = [
    Role.HqManager,
    Role.AreaManager,
    Role.AreaManagerApproval,
    Role.LocationManager,
    Role.SuperHqInternal,
    Role.SuperHqExternal,
  ];

  const getOtp2faCode = async () => {
    try {
      const res = await authApi.getOtp2fa();

      if (res.data.status) {
        userStorage.set(res.data.user);
        dispatch(authActions.updateUser(res.data.user));
      }

      
    } catch (error) {
      console.error('OTP verification error:', error);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>();
  const onSubmit: SubmitHandler<FormData> = async (data, e) => {
    try {
      const { remember, email, password, tfa_code } = data;
          const res = await authApi.login(email, password, tfa_code);
          httpClient.setAuthorization(res.data.accessToken, 'Bearer');

          rememberMeStorage.set({ remember: remember, accessTimes: 1 });

          authStorage.set({ accessToken: res.data.accessToken }, secondToHours(res.data.expiresIn));
          userStorage.set(res.data.user);
          dispatch(authActions.authen(res.data));

          const currentUser = res.data.user;
          if (i18n.language !== currentUser.language_code) {
            i18n.changeLanguage(currentUser.language_code ?? Language.English);
          }

          if (res.data.hqUser) {
            hqStorage.set(res.data.hqUser);
            dispatch(setHq(res.data.hqUser));
          }

          const shouldUse2FA = 
              (react2fa === 'ALL') ||
              (react2fa === 'INTERNAL' && !partnerRoles.includes(res.data.user.user_type)) ||
              (react2fa === 'PARTNER' && partnerRoles.includes(res.data.user.user_type));

          if (shouldUse2FA) {
            if (isTwoFaVerified) {
              router.push(PATH_NAME.DASHBOARD);
            } else {
              if (currentUser.tfa_method !== null) {
                getOtp2faCode();
                router.push(PATH_NAME.AUTH_OTP);
                
              } else {
                router.push(PATH_NAME.SELECT_2FA_METHOD);
              }
            }
          } else {
            router.push(PATH_NAME.DASHBOARD);
          }
    } catch (error: any) {
      notiFailed(error.message);
    }
  };
  
  useLayoutEffect(() => {
    return () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
  }, []);

  return (
    <>
      {device === DeviceMode.Desktop && (
        <Center h="100vh" bg={addHexOpacity(colors.tango[500], 85)}>
          <Box
            position="absolute"
            top="0"
            width="100vw"
            height="100vh"
            bgImage={PuzzleBackground}
            bgSize="cover"
            bgRepeat="no-repeat"
            bgPosition="center"
            zIndex={0}
          />
          <Box
            position="absolute"
            bottom="0"
            left="0"
            width="100%"
            height="60%"
            bgImage={BottomSpiral}
            p={0}
            bgSize="cover"
            bgRepeat="no-repeat"
            bgPosition="center"
            zIndex={0}
          >
            <Image src={WelcomeToJodTitle} alt="Welcome to JOD Title" mt={'11.5%'} ml={'9%'} />
          </Box>
          <SlideFade in={true} offsetY="-20px">
            <HStack spacing="10px" top={'40px'} left={'40px'} position={'absolute'}>
              <Icon w="8%" height="auto" as={LogoIcon} bgColor={colors.white} borderRadius={'8px'} padding={'1%'} />
              <Icon w="12.5%" height="auto" as={JodTextWhite} />
            </HStack>
            <Card
              shadow="2xl"
              p="40px"
              w="372px"
              maxW="372px"
              position={'absolute'}
              left={'65%'}
              top={'35%'}
              borderRadius={'20px'}
            >
              <Typography.Title
                fontSize={{ base: 'md', lg: '24px' }}
                lineHeight={'31.25px'}
                mb={3}
              >
                Welcome
              </Typography.Title>
              <Typography.Text
                fontSize={{ base: 'md', lg: '14px' }}
                mb={5}
                color={colors.vulcan[400]}
                lineHeight={'21px'}
              >
                  Sign in to start managing your workforce
                </Typography.Text><form onSubmit={handleSubmit(onSubmit)}>
                  <FormControl isInvalid={!!errors.email}>
                    <Input
                      id="email"
                      {...register('email', {
                        required: 'Please enter email.',
                        validate: (value: string) => {
                          const isValid = isEmail(value);
                          if (isValid) return undefined;
                          return 'The email must be a valid email address.';
                        },
                      })}
                      bgColor={addHexOpacity(colors.lightGrey, 50)}
                      border={'none'}
                      placeholder="Email"
                      autoComplete="username"
                      height={'53px'} />
                    <FormErrorMessage mt="4px" position="absolute">
                      {errors.email?.message}
                    </FormErrorMessage>
                  </FormControl>
                  <FormControl isInvalid={!!errors.password} mt="26px">
                    <PasswordInput
                      id="password"
                      {...register('password', {
                        required: 'Please enter password.',
                      })}
                      placeholder="Password"
                      autoComplete="current-password"
                      bgColor={addHexOpacity(colors.lightGrey, 50)}
                      border={'none'}
                      height={'53px'} />
                    <FormErrorMessage mt="6px" lineHeight="1">
                      {errors.password?.message}
                    </FormErrorMessage>
                  </FormControl>
                  <Box mt="26px">
                    <PrimaryButton uppercase={false} w="100%" isLoading={isSubmitting} type="submit" h={53} fontSize={'14px'}>
                      <Flex w="100%" align="center">
                        <span>Sign In</span>
                        <Spacer />
                        <Icon as={CaretRightIcon} />
                      </Flex>
                    </PrimaryButton>
                    <Link to={PATH_NAME.FORGOT_PASSWORD}>
                      <Typography.Title
                        mt="26px"
                        cursor="pointer"
                        color="tango.400"
                        _hover={{ color: 'primary', textDecoration: 'underline' }}
                        fontSize={{ base: '11px', md: '14px', lg: '13px', '2xl': '14px' }}
                      >
                        I forgot my password
                      </Typography.Title>
                    </Link>
                  </Box>
                </form>
            </Card>
          </SlideFade>
        </Center>
      )}
      {device !== DeviceMode.Desktop && (
        <>
          <Box position="relative" h="100vh" w="100vw" bg={addHexOpacity(colors.tango[500], 85)}>
            <Box
              position="absolute"
              top="0"
              width="100%"
              height="50%"
              bgImage={PuzzleBackground}
              bgSize="cover"
              bgRepeat="no-repeat"
              bgPosition="center"
              filter="blur(5px)"
              zIndex={0}
            />
            <Box
              position="absolute"
              top="15%"
              width="100%"
              display="flex"
              justifyContent="center"
              alignItems="center"
              zIndex={1}
            >
              <Icon w="152px" height="73px" as={JodTextWhite} />
            </Box>
            <Box
              position="absolute"
              bottom="0"
              width="100%"
              bgColor={colors.white}
              borderTopLeftRadius="20px"
              borderTopRightRadius="20px"
              p="16.5% 11.5%"
              height="65%"
              overflow="hidden"
              zIndex={1}
            >
              <Box mb={3}>
                <Typography.Title fontSize="24px" lineHeight="31.25px">
                  Welcome
                </Typography.Title>
              </Box>
              <Box mb={5}>
                <Typography.Text fontSize="14px" color={colors.vulcan[400]} lineHeight="21px">
                  Sign in to start managing your workforce
                </Typography.Text>
              </Box>
              <form onSubmit={handleSubmit(onSubmit)}>
                <FormControl isInvalid={!!errors.email}>
                  <Input
                    id="email"
                    {...register('email', {
                      required: 'Please enter email.',
                      validate: (value) => {
                        const isValid = isEmail(value);
                        if (isValid) return undefined;
                        return 'The email must be a valid email address.';
                      },
                    })}
                    bgColor={addHexOpacity(colors.lightGrey, 50)}
                    border="none"
                    placeholder="Email"
                    autoComplete="username"
                    height="53px"
                  />
                  <FormErrorMessage mt="4px" position="absolute">
                    {errors.email?.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!errors.password} mt="26px">
                  <PasswordInput
                    id="password"
                    {...register('password', {
                      required: 'Please enter password.',
                    })}
                    placeholder="Password"
                    autoComplete="current-password"
                    bgColor={addHexOpacity(colors.lightGrey, 50)}
                    border="none"
                    height="53px"
                  />
                  <FormErrorMessage mt="6px" lineHeight="1">
                    {errors.password?.message}
                  </FormErrorMessage>
                </FormControl>
                <Box mt="26px">
                  <PrimaryButton uppercase={false} w="100%" isLoading={isSubmitting} type="submit" h="53px" fontSize="14px">
                    <Flex w="100%" align="center">
                      <span>Sign In</span>
                      <Spacer />
                      <Icon as={CaretRightIcon} />
                    </Flex>
                  </PrimaryButton>
                  <Link to={PATH_NAME.FORGOT_PASSWORD}>
                    <Typography.Title
                      mt="26px"
                      cursor="pointer"
                      color="tango.400"
                      _hover={{ color: 'primary', textDecoration: 'underline' }}
                      fontSize="12px"
                    >
                      I forgot my password
                    </Typography.Title>
                  </Link>
                </Box>
              </form>
            </Box>
          </Box>
        </>
      )}
    </>
  );

};

export default Login;
